"use client";

import Link from "next/link";
import { ArrowRight, Building2, LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { AuthCallout, AuthHeading, AuthLayout, StatusPanel } from "@/components/auth-ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getErrorCopy } from "@/lib/error-config";
import { RegistrationError, clearSignupIdentity, saveSignupOtp, useRegistrationService } from "@/lib/registration-service";

const REGISTRATION_NUMBER = /^[A-Za-z0-9/-]{1,50}$/;

/* Step 1 of onboarding_v3, started from the facility: the registry names the
   administrator and the code goes to their client-registry contact. No login
   required. */
export default function SignupVerifyPage() {
  const router = useRouter();
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [error, setError] = useState("");
  const [failure, setFailure] = useState<RegistrationError | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const registration = useRegistrationService();

  // Legacy resume links (?facility_id=…&step=5) point at step 5, which lives
  // at /sign-up/setup - this route only ever renders step 1. Forward them.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("step") === "5") {
      const fid = params.get("facility_id");
      router.replace(fid ? `/sign-up/setup?facility_id=${encodeURIComponent(fid)}` : "/sign-up/setup");
    }
  }, [router]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setError(""); setFailure(null);
    const value = registrationNumber.trim();
    if (!value) { setError("Enter the facility registration number."); return; }
    if (!REGISTRATION_NUMBER.test(value)) { setError("Use only the letters, numbers, slashes and dashes of the registration number."); return; }
    setIsChecking(true);
    try {
      // The administrator's ID stays server-side behind registrationSession
      // until the code is verified. Passwords never enter storage.
      const session = await registration.verifyRegistration(value);
      clearSignupIdentity();
      saveSignupOtp({
        idType: session.identificationType,
        idNumber: "",
        session,
        registrationSession: session.registrationSession,
        registrationNumber: session.registrationNumber,
        facilityName: session.facilityName,
      });
      router.push("/sign-up/otp");
    } catch (registrationError) {
      setFailure(registrationError instanceof RegistrationError ? registrationError : new RegistrationError("UNKNOWN", 0));
      setIsChecking(false);
    }
  };

  return <AuthLayout variant="sign-up" step={1}>
    <AuthHeading overline="Step 1 of 7" title="Find Your Facility">Start with the facility’s registration number. We’ll find the administrator recorded in the facility registry and send them a code.</AuthHeading>
    <form className="auth-stack" onSubmit={submit} noValidate>
      <div className="auth-field">
        <label className="auth-label" htmlFor="registration-number">Facility registration number</label>
        <div className="auth-input-icon"><Building2 size={18} /><Input id="registration-number" value={registrationNumber} onChange={(event) => { setError(""); setFailure(null); setRegistrationNumber(event.target.value); }} placeholder="Enter the registration number" autoComplete="off" inputMode="text" aria-invalid={Boolean(error)} aria-describedby={error ? "registration-number-error registration-number-hint" : "registration-number-hint"} /></div>
        <span id="registration-number-hint" className="auth-hint">The number your regulator issued, as recorded in the facility registry.</span>
        {error && <span id="registration-number-error" className="field-error">{error}</span>}
      </div>
      {failure && <StatusPanel tone="danger" title={getErrorCopy(failure.code).title}>{failure.detail ?? getErrorCopy(failure.code).message}</StatusPanel>}
      <AuthCallout title="Administrator Access Required">The code goes to the email of the administrator recorded for this facility. If those details are out of date, ask the regulator to update them first.</AuthCallout>
      <Button type="submit" disabled={isChecking}>{isChecking ? <><LoaderCircle size={16} className="spin" />Checking official records…</> : <>Continue <ArrowRight size={16} /></>}</Button>
      <p className="auth-switch"><span>Already registered?</span><Link href="/sign-in">Sign in</Link></p>
    </form>
  </AuthLayout>;
}
